import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { EDeliverableType } from '@enums/campaign.enum';
import { Observable } from 'rxjs';
import { AudioTrackingService } from './audio-tracking/audio-tracking.service';
import { CaptionService } from './caption/caption.service';
import { ChatbotMultiService } from './chatbot-multi/chatbot-multi.service';
import { ChatbotService } from './chatbot/chatbot.service';
import { ContentService } from './content/content.service';
import { CustomService } from './custom/custom.service';
import {
  IDeliverableConfiguration,
  TDeliverable,
  TDeliverableConfigMap,
  TDeliverableForm,
} from './deliverable-list.interface';
import { DescriptionService } from './description/description.service';
import { GamePlayService } from './game-play/game-play.service';
import { HashtagService } from './hashtag/hashtag.service';
import { MentionService } from './mention/mention.service';
import { MessageService } from './message/message.service';
import { ProductPlacementService } from './product-placement/product-placement.service';
import { ShowcaseService } from './showcase/showcase.service';
import { TalkingPointAiService } from './talking-point-ai/talking-point-ai.service';
import { TalkingPointService } from './talking-point/talking-point.service';
import { TextTrackingService } from './text-tracking/text-tracking.service';
import { TitleService } from './title/title.service';
import { VisualService } from './visual/visual.service';

@Injectable({
  providedIn: 'root',
})
export class DeliverableListService {
  private readonly titleService = inject(TitleService);
  private readonly gamePlayService = inject(GamePlayService);
  private readonly chatbotService = inject(ChatbotService);
  private readonly chatbotMultiService = inject(ChatbotMultiService);
  private readonly talkingPointAiService = inject(TalkingPointAiService);
  private readonly talkingPointService = inject(TalkingPointService);
  private readonly visualService = inject(VisualService);
  private readonly productPlacementService = inject(ProductPlacementService);
  private readonly showcaseService = inject(ShowcaseService);
  private readonly textTrackingService = inject(TextTrackingService);
  private readonly audioTrackingService = inject(AudioTrackingService);
  private readonly captionService = inject(CaptionService);
  private readonly hashtagService = inject(HashtagService);
  private readonly mentionService = inject(MentionService);
  private readonly messageService = inject(MessageService);
  private readonly descriptionService = inject(DescriptionService);
  private readonly contentService = inject(ContentService);
  private readonly customService = inject(CustomService);

  private readonly deliverableConfigs: TDeliverableConfigMap = {
    [EDeliverableType.title]: this.titleService.getConfig(),
    [EDeliverableType.gamePlay]: this.gamePlayService.getConfig(),
    [EDeliverableType.chatBot]: this.chatbotService.getConfig(),
    [EDeliverableType.chatbotMulti]: this.chatbotMultiService.getConfig(),
    [EDeliverableType.talkingPoint]: this.talkingPointService.getConfig(),
    [EDeliverableType.talkingPointAi]: this.talkingPointAiService.getConfig(),
    [EDeliverableType.visual]: this.visualService.getConfig(),
    [EDeliverableType.productPlacement]: this.productPlacementService.getConfig(),
    [EDeliverableType.showcase]: this.showcaseService.getConfig(),
    [EDeliverableType.textTracking]: this.textTrackingService.getConfig(),
    [EDeliverableType.audioTracking]: this.audioTrackingService.getConfig(),
    [EDeliverableType.caption]: this.captionService.getConfig(),
    [EDeliverableType.hashTag]: this.hashtagService.getConfig(),
    [EDeliverableType.mention]: this.mentionService.getConfig(),
    [EDeliverableType.message]: this.messageService.getConfig(),
    [EDeliverableType.description]: this.descriptionService.getConfig(),
    [EDeliverableType.content]: this.contentService.getConfig(),
    [EDeliverableType.custom]: this.customService.getConfig(),
  };

  private readonly http = inject(HttpClient);

  public createDeliverable(
    type: EDeliverableType,
    item?: TDeliverable,
    creatorId?: string,
    isViewMode?: boolean,
  ): FormGroup<TDeliverableForm> | null {
    const config = this.deliverableConfigs[type];

    if (!config) {
      return null;
    }

    return config.createForm(item, creatorId, isViewMode) as FormGroup<TDeliverableForm>;
  }

  public getAvailableDeliverableConfigurations(): Observable<IDeliverableConfiguration[]> {
    return this.http.get<IDeliverableConfiguration[]>('/deliverable-configurations?active=true');
  }
}
